// Shared waitlist submit — the one place that POSTs to the Worker and, on success,
// navigates to /joined (tier carried in router state). Both the inline EmailCapture
// and the RequestAccessModal use this, so the success behaviour can't drift apart.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export type WaitlistTier = 'monthly' | 'annual' | 'founding' | 'general';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SubmitState = 'idle' | 'sending' | 'error';

export function useWaitlistSubmit(tier: WaitlistTier = 'general', source?: string) {
  const navigate = useNavigate();
  const [state, setState] = useState<SubmitState>('idle');

  // `company` is the honeypot field (real users never fill it).
  const submit = async (email: string, company = ''): Promise<boolean> => {
    if (state === 'sending') return false;
    const value = email.trim();
    if (!EMAIL_RE.test(value)) { setState('error'); return false; }
    setState('sending');
    try {
      const r = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, tier, source, company }),
      });
      if (!r.ok) throw new Error(String(r.status));
      // replace so the Back button lands on the page they came from, not a stale form
      navigate('/joined', { state: { tier }, replace: true });
      return true;
    } catch {
      setState('error');
      return false;
    }
  };

  const clearError = () => { if (state === 'error') setState('idle'); };

  return { state, submit, clearError };
}
